// src/components/ui/Button.tsx — variant-driven button.
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { C, F } from "../../theme";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "soft"
  | "heather"
  | "sage"
  | "danger"
  | "ghost";

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  variant?: ButtonVariant;
  children: ReactNode;
  fullWidth?: boolean;
  type?: "button" | "submit" | "reset";
}

const baseStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  fontFamily: F.body,
  fontWeight: 800,
  fontSize: 15,
  padding: "12px 16px",
  borderRadius: 12,
  minHeight: 48,
  cursor: "pointer",
  textDecoration: "none",
  border: "none",
  letterSpacing: 0.2,
  WebkitTapHighlightColor: "transparent",
  touchAction: "manipulation",
};

const variantStyle = (variant: ButtonVariant): CSSProperties => {
  switch (variant) {
    case "primary":
      return {
        background: C.brassDk,
        color: C.brassGlow,
        boxShadow: "inset 0 1px 0 rgba(244,226,168,0.3), 0 2px 6px rgba(122,84,11,0.15)",
      };
    case "secondary":
      return {
        background: C.card,
        color: C.brassDk,
        border: `1px solid ${C.hairline}`,
        boxShadow: C.shadowS,
      };
    case "soft":
      return {
        background: C.brassBg,
        color: C.brassDk,
        border: "1px solid rgba(167,117,28,0.18)",
      };
    case "heather":
      return {
        background: C.heather,
        color: "#FFFCEC",
        boxShadow: "0 2px 6px rgba(86,64,106,0.18)",
      };
    case "sage":
      return {
        background: C.sage,
        color: "#FFFCEC",
        boxShadow: "0 2px 6px rgba(78,104,82,0.18)",
      };
    case "danger":
      return {
        background: C.signalBg,
        color: C.signalDk,
        border: "1px solid rgba(184,58,43,0.22)",
      };
    case "ghost":
    default:
      return { background: "transparent", color: C.inkSoft };
  }
};

export const Button = ({
  variant = "primary",
  children,
  fullWidth,
  type = "button",
  style,
  disabled,
  ...rest
}: ButtonProps) => {
  const finalStyle: CSSProperties = {
    ...baseStyle,
    ...variantStyle(variant),
    ...(fullWidth ? { width: "100%" } : null),
    ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : null),
    ...style,
  };
  return (
    <button type={type} disabled={disabled} style={finalStyle} {...rest}>
      {children}
    </button>
  );
};
